import { useContext, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@appkit4/react-components/badge';
import { Button } from '@appkit4/react-components/button';
import { Search } from '@appkit4/react-components/search';
import { Tabs, Tab } from '@appkit4/react-components/tabs';
import { TextArea } from '@appkit4/react-components/field';
import { Upload } from '@appkit4/react-components/upload';
import { Modal } from '@appkit4/react-components/modal';
import { Tooltip } from '@appkit4/react-components/tooltip';
import { SessionCard } from '@components/SessionCard';
import { SessionListItem } from '@components/SessionListItem';
import { SessionContext } from '@contexts/SessionProvider';
import { UserContext } from '@contexts/UserProvider';
import { useFileProcessor } from '@hooks/index';
import DesignSystemBadge from '../../components/DesignSystemBadge';
//import { PreferencesContext } from '@contexts/PreferencesProvider';

import {
    createUserSession,
    generateSessionTopic,
    getAllSessions,
    getSharedSessions,
    renameSessionTopic,
    updateFavoriteSession,
    abortActiveStream,
} from '@config/getSessionApis';
import { handleGenerate } from '@utils/generateHandler';
import RapidCraftMasthead from '../../assets/images/rapidcraft-masthead.svg';
import styles from './Dashboard.module.scss';
import { Select } from '@appkit4/react-components';
// import { SessionsTable } from '@components/SessionsTable/SessionsTable';
// import { SessionsTable } from '@components/SessionsTable/SessionsTable';

const Dashboard = () => {
    const {
        attachFile,
        attachDataFile,
        activeLayoutTab,
        activeSessionTab,
        sortBy,
        designSystemFilter,
        designSystem,
        templateVersion,
        setTemplateVersion,
        projectCount,
        modificationOptions,
        currentGenerationId,
        isProcessingAttachment,
        attachmentProcessingError,
        processedImage,
        processedDataToon,
        processedDataFileName,
        setCurrentGenerationId,
        setAttachFile,
        setAttachDataFile,
        setActiveLayoutTab,
        setActiveSessionTab,
        setCurrentSource,
        setFirstPrompt,
        setHistory,
        setIsNewSession,
        setSessionName,
        setIsSandboxLoading,
        setIsGenerateComplete,
        setSortBy,
        setDesignSystemFilter,
        setThread,
        setSessionId,
        setDesignSystem,
        setStreamMessageProvider,
        setCurrentChatSessionId,
        setShowCodeStream,
        setStreamMessagesConsumer,
        setCurrentHistoryCommit,
        setSessionOwnerEmail,
        setGenerationError,
        setAttachmentProcessingError,
        setToolStatus,
        setGenerationStatus,
        setPostProcessingFailed,
    } = useContext(SessionContext);
    const { userEmail } = useContext(UserContext);

    useFileProcessor();

    const navigate = useNavigate();

    const [prompt, setPrompt] = useState('');

    const [searchValue, setSearchValue] = useState('');

    const [activeSessions, setActiveSessions] = useState<any[]>([]);
    const [allSessions, setAllSessions] = useState<any[]>([]);
    const [prefetchedSharedSessions, setPrefetchedSharedSessions] = useState<any[]>([]);

    const [showAttachModal, setShowAttachModal] = useState<boolean>(false);
    const [showAttachFileModal, setShowAttachFileModal] = useState<boolean>(false);
    const [showAttachMenu, setShowAttachMenu] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const sessionRef = useRef(null);
    const attachMenuRef = useRef<HTMLDivElement>(null);
    const attachButtonRef = useRef<HTMLDivElement>(null);
    const sortList = [
        { label: 'Last edited', value: 'lastEdited' },
        { label: 'Created date', value: 'createdAt' },
        { label: 'A-Z', value: 'ascending' },
        { label: 'Z-A', value: 'descending' },
    ];

    const designSystemList = [
        { label: 'Select all', value: 'Select all', iconName: null },
        { label: 'Appkit', value: 'Appkit', iconName: 'AppkitIcon' },
        { label: 'Appkit (rebrand)', value: 'AppkitRebrand', iconName: 'AppkitRebrandIcon' },
        { label: 'Fluent', value: 'Fluent', iconName: 'FluentIcon' },
        { label: 'Material', value: 'MaterialUI', iconName: 'MaterialIcon' },
        { label: 'New Era', value: 'NewEra', iconName: 'NewEraIcon' },
    ];

    useEffect(() => {
        getSharedSessions(userEmail).then((result) => {
            setPrefetchedSharedSessions(result.resultObject || []);
        });
    }, [userEmail]);

    useEffect(() => {
        setDesignSystem('MaterialUI');
        setTemplateVersion('v2');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: This effect should only run on mount to initialize design system and template version

    const sortSessions = (): any[] => {
        const sessions = allSessions;
        const sortByValue = sortBy.value;
        const designSystemValue = designSystemFilter.value;
        let sorted = [];
        switch (sortByValue) {
            case 'lastEdited':
                sorted = [...sessions].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
                break;
            case 'createdAt':
                sorted = [...sessions].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                break;
            case 'ascending':
                sorted = [...sessions].sort((a, b) => a.topic?.localeCompare(b?.topic));

                break;
            case 'descending':
                sorted = [...sessions].sort((a, b) => b.topic?.localeCompare(a?.topic));
                break;
            default:
                break;
        }
        return sorted.filter((session) => {
            if (designSystemValue === 'Select all') {
                return true;
            }
            return (
                session.designSystem &&
                session.designSystem.trim().toLowerCase() === designSystemValue.trim().toLowerCase()
            );
        });
    };

    const fetchSessions = async (email: string, activeSessionTab: number, searchValue: string) => {
        let baseSessions: any[] = [];

        try {
            const mySessionsResult = await getAllSessions(email);
            const mySessions = mySessionsResult.resultObject;
            if (!mySessions) {
                return;
            }

            mySessions.map((session: any) => {
                session.updatedAt = new Date(session.updatedAt).getTime();
                session.createdAt = new Date(session.createdAt).getTime();
            });

            if (activeSessionTab === 0) {
                baseSessions = [...mySessions];
            } else if (activeSessionTab === 1) {
                baseSessions =
                    prefetchedSharedSessions.length > 0
                        ? [...prefetchedSharedSessions]
                        : (await getSharedSessions(email)).resultObject || [];

                // Ensure shared sessions have converted timestamps
                baseSessions.forEach((session: any) => {
                    if (typeof session.updatedAt === 'string') {
                        session.updatedAt = new Date(session.updatedAt).getTime();
                    }
                    if (typeof session.createdAt === 'string') {
                        session.createdAt = new Date(session.createdAt).getTime();
                    }
                });
            } else if (activeSessionTab === 2) {
                baseSessions = mySessions.filter((session: any) => session.favorite);
            }

            if (searchValue.trim()) {
                // Case-insensitive partial match on topic
                baseSessions = baseSessions.filter((s) => s?.topic.toLowerCase().includes(searchValue.toLowerCase()));
            }
            setAllSessions(baseSessions);
            const sortedSessions: any[] = sortSessions();
            setActiveSessions(sortedSessions);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        setThread([]);
        setPrompt('');
        setHistory([]);
        setCurrentSource({});
        setIsSandboxLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: Setter functions are stable - this effect should only run on mount

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                attachMenuRef.current &&
                !attachMenuRef.current.contains(event.target as Node) &&
                attachButtonRef.current &&
                !attachButtonRef.current.contains(event.target as Node)
            ) {
                setShowAttachMenu(false);
            }
        };

        const handleScroll = () => {
            setShowAttachMenu(false);
        };

        const handleResize = () => {
            setShowAttachMenu(false);
        };

        if (showAttachMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [showAttachMenu]);

    const handleAttachButtonClick = () => {
        if (attachButtonRef.current) {
            const rect = attachButtonRef.current.getBoundingClientRect();
            // Position menu above the button
            const menuHeight = 100; // Approximate height of menu with 2 items
            setMenuPosition({
                top: rect.top + window.scrollY - menuHeight - 4,
                left: rect.left + window.scrollX,
            });
        }
        setShowAttachMenu(!showAttachMenu);
    };

    useEffect(() => {
        fetchSessions(userEmail, activeSessionTab, searchValue);
    }, [activeSessionTab, searchValue, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: fetchSessions is a stable function defined in this component

    useEffect(() => {
        const sortedSessions: any[] = sortSessions();
        setActiveSessions(sortedSessions);
    }, [sortBy, designSystemFilter, allSessions]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: sortSessions is a stable function defined in this component

    const handleToggleFavorite = (index: number) => {
        updateFavoriteSession(userEmail, activeSessions[index].sessionId, !activeSessions[index].favorite);
        setActiveSessions((prev) => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                // Flip string from 'true' to 'false' or vice versa
                favorite: !copy[index].favorite,
            };
            return copy;
        });
    };

    const handleDeleteSession = (sessionId: string) => {
        setActiveSessions((prev) => prev.filter((item) => item.sessionId !== sessionId));
    };

    const handleDuplicateSession = () => {
        if (activeSessionTab === 1) {
            setActiveSessionTab(0);
            fetchSessions(userEmail, 0, searchValue);
        } else {
            fetchSessions(userEmail, activeSessionTab, searchValue);
        }
    };

    const handleChange = (value: string) => {
        setPrompt(value);
        setFirstPrompt(value);
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && !event.shiftKey && event.type === 'keydown') {
            event.preventDefault();
            handleGenerateClick();
        }
    };

    const handleGenerateClick = async () => {
        if (prompt.trim().length > 0) {
            setPrompt('');
            setIsNewSession(false);
            let newSessionId = '';
            const sessionResult = await createUserSession(userEmail, designSystem, templateVersion);
            const sessionId = sessionResult.resultObject.sessionId;
            if (sessionId) {
                setSessionId(sessionId);
                newSessionId = sessionId;
                setSessionOwnerEmail(userEmail);
            }
            const newSessionTopic = await generateSessionTopic(
                userEmail,
                newSessionId,
                prompt,
                projectCount > 1,
                attachFile,
                attachDataFile
            );
            const topic = newSessionTopic.resultObject.payload.topic;
            if (!topic) {
                confirm('Generate topic failed');
                return;
            }
            setSessionName(topic);
            setThread([]);
            try {
                navigate(
                    `/session?query=generate&sessionId=${sessionId}&designSystem=${designSystem}&templateVersion=${templateVersion}`
                );
                setIsGenerateComplete(false);
                setShowCodeStream(true);
                setPostProcessingFailed(false);
                handleGenerate(
                    prompt,
                    userEmail,
                    attachFile,
                    attachDataFile,
                    modificationOptions,
                    projectCount,
                    designSystem,
                    templateVersion,
                    newSessionId,
                    currentGenerationId,
                    setCurrentGenerationId,
                    setCurrentSource,
                    setCurrentChatSessionId,
                    setStreamMessageProvider,
                    setIsGenerateComplete,
                    setShowCodeStream,
                    setStreamMessagesConsumer,
                    setHistory,
                    setThread,
                    setCurrentHistoryCommit,
                    setGenerationError,
                    setToolStatus,
                    setGenerationStatus,
                    setPostProcessingFailed,
                    undefined,
                    undefined,
                    processedImage,
                    processedDataToon,
                    processedDataFileName
                );
            } catch (error) {
                console.error('Error fetching data from API: ', error);
            }
        } else {
            confirm('You must provide a prompt with at least one word');
        }
    };

    const handleSessionTabChange = (index: number) => {
        setActiveSessionTab(index);
    };

    const handleLayoutTabChange = (index: number) => {
        setActiveLayoutTab(index);
    };

    const handleSortBySelect = (value: any) => {
        const res: any = sortList.find((item) => item.value === value);
        setSortBy(res);
    };

    const handleDesignSystemFilter = (value: any) => {
        const res: any = designSystemList.find((item) => item.value === value);
        setDesignSystemFilter(res);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    const handleSessionClick = async (session: any) => {
        abortActiveStream();
        setSessionName(session.topic);
        setThread([]);
        setHistory([]);
        setCurrentSource({});
        setSessionOwnerEmail(session.email || '');
        setStreamMessageProvider([]);
        setStreamMessagesConsumer([]);
        setShowCodeStream(false);
        setIsGenerateComplete(true);
        setGenerationStatus(null);
        setToolStatus(null);
        setCurrentGenerationId('');
        const sessionTemplateVersion = session.templateVersion || 'v2';
        navigate(
            `/session?query=get&sessionId=${session.sessionId}&designSystem=${session.designSystem}&templateVersion=${sessionTemplateVersion}`
        );
    };

    const handleUploadChange = (file: any) => {
        const originFile = file instanceof File ? file : file?.originFile;
        setAttachFile(originFile);
        setShowAttachModal(false);
    };

    const handleDataFileUploadChange = (file: any) => {
        const originFile = file instanceof File ? file : file?.originFile;
        setAttachDataFile(originFile);
        setShowAttachFileModal(false);
    };

    const handleUpdateSessionName = (topic: string, index: number) => {
        renameSessionTopic(userEmail, activeSessions[index].sessionId, topic);
        setActiveSessions((prev) => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                topic: topic,
            };
            return copy;
        });
        (sessionRef.current as any).setIsEditing(false);
    };

    const onSelectSystem = (value: any) => {
        setDesignSystem(value);
    };

    const DesignSystemSelector = () => {
        const data = [
            { value: 'Appkit', label: 'Appkit', iconName: 'AppkitIcon' },
            { value: 'AppkitRebrand', label: 'Appkit (rebrand)', iconName: 'AppkitRebrandIcon' },
            { value: 'Fluent', label: 'Fluent', iconName: 'FluentIcon' },
            { value: 'MaterialUI', label: 'Material', iconName: 'MaterialIcon' },
            { value: 'NewEra', label: 'New Era', iconName: 'NewEraIcon' },
        ];
        const selectedItem = data.find((item) => item.value === designSystem) || data[0];

        return (
            <Select
                data={data}
                placeholder="Dropdown"
                required={false}
                value={designSystem}
                className="design-system-selector"
                dropdownClassName="design-system-dropdown-overlay"
                onSelect={onSelectSystem}
                dropdownRenderMode="portal"
                hideTitleOnInput={true}
                valueTemplate={() => {
                    return (
                        <Tooltip
                            trigger="hover"
                            position="top"
                            distance={4}
                            id="ChooseDesignSystemTooltip"
                            appendAfterTarget={false}
                            content="Choose Design system">
                            <div className={styles['design-system-label-group']}>
                                <div>{selectedItem?.label}</div>
                                {selectedItem && selectedItem.iconName && (
                                    <DesignSystemBadge iconName={selectedItem.iconName} size="md" />
                                )}
                            </div>
                        </Tooltip>
                    );
                }}
                suffixTemplate={(item) => {
                    return item.iconName && <DesignSystemBadge iconName={item.iconName} size="md" />;
                }}></Select>
        );
    };

    return (
        <div className={styles.dashboard}>
            <div className={styles['welcome-wrapper']}>
                <RapidCraftMasthead />
                <div className={styles['title-wrapper']}>
                    <h1>Welcome to Design Draft</h1>
                    <Badge value="Beta" type="primary" />
                </div>
                <p>Quickly craft designs in minutes. Start generating ideas.</p>
            </div>
            <div className={styles['prompt-container']}>
                <div className={styles['prompt-wrapper']}>
                    <div className={styles['textarea-wrapper']}>
                        <TextArea
                            className={styles['textarea']}
                            autosize={{
                                maxRows: prompt ? 4 : 2,
                                minRows: 3,
                            }}
                            value={prompt}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            title="What are you building?"
                        />
                    </div>
                    <div className={styles['button-wrapper']}>
                        {/* temp hide as backend thmee function has not finished */}
                        {/* <div className={styles['pref-icon-container']}>
                        <span className="Appkit4-icon icon-art-themes-outline" onClick={handleClickPreferences}></span>
                    </div> */}
                        <DesignSystemSelector></DesignSystemSelector>
                        <div className={styles['attach-buttons-group']} ref={attachButtonRef}>
                            <Button
                                kind="tertiary"
                                icon="icon-paperclip-outline"
                                aria-label="Attach files button"
                                role="button"
                                onClick={handleAttachButtonClick}
                                className={styles['attach-button']}>
                                Attach
                            </Button>
                        </div>
                        {showAttachMenu &&
                            menuPosition &&
                            createPortal(
                                <div
                                    ref={attachMenuRef}
                                    className={styles['attach-menu']}
                                    style={{
                                        position: 'absolute',
                                        top: `${menuPosition.top}px`,
                                        left: `${menuPosition.left}px`,
                                    }}>
                                    <div
                                        className={`${styles['attach-menu-item']} ${attachFile ? styles['disabled'] : ''}`}
                                        onClick={() => {
                                            if (!attachFile) {
                                                setShowAttachModal(true);
                                                setShowAttachMenu(false);
                                            }
                                        }}>
                                        <span className="Appkit4-icon icon-image-outline"></span>
                                        <span>Attach image</span>
                                    </div>
                                    <div
                                        className={`${styles['attach-menu-item']} ${attachDataFile ? styles['disabled'] : ''}`}
                                        onClick={() => {
                                            if (!attachDataFile) {
                                                setShowAttachFileModal(true);
                                                setShowAttachMenu(false);
                                            }
                                        }}>
                                        <span className="Appkit4-icon icon-document-file-outline"></span>
                                        <span>Attach data</span>
                                    </div>
                                </div>,
                                document.body
                            )}
                        <Button
                            kind="primary"
                            icon="icon-paper-airplane-outline"
                            className="pink-button"
                            onClick={handleGenerateClick}
                            disabled={prompt.trim().length === 0 || isProcessingAttachment}
                            aria-label="Generate button"
                            role="button">
                            Generate
                        </Button>
                    </div>
                    {attachmentProcessingError && (
                        <div className={styles['attachment-error']}>
                            <span className="Appkit4-icon icon-alert-outline"></span>
                            <span>{attachmentProcessingError}</span>
                            <span
                                onClick={() => setAttachmentProcessingError(null)}
                                className="Appkit4-icon icon-close-outline"
                                role="button"
                                aria-label="Dismiss error"></span>
                        </div>
                    )}
                    <div
                        className={`${styles['attached-file']} ${attachFile && isProcessingAttachment ? styles['processing'] : ''}`}
                        style={{ display: attachFile ? 'flex' : 'none' }}>
                        <div>
                            {attachFile && isProcessingAttachment ? (
                                <span className={styles['spinner']}></span>
                            ) : (
                                <span className="Appkit4-icon icon-image-outline"></span>
                            )}
                            <span className={styles.file}>{attachFile && attachFile.name}</span>
                        </div>
                        <span onClick={() => setAttachFile(null)} className="Appkit4-icon icon-close-outline"></span>
                    </div>
                    <div
                        className={`${styles['attached-file']} ${attachDataFile && isProcessingAttachment ? styles['processing'] : ''}`}
                        style={{ display: attachDataFile ? 'flex' : 'none' }}>
                        <div>
                            {attachDataFile && isProcessingAttachment ? (
                                <span className={styles['spinner']}></span>
                            ) : (
                                <span className="Appkit4-icon icon-document-file-outline"></span>
                            )}
                            <span className={styles.file}>{attachDataFile && attachDataFile.name}</span>
                        </div>
                        <span
                            onClick={() => setAttachDataFile(null)}
                            className="Appkit4-icon icon-close-outline"></span>
                    </div>
                </div>
            </div>
            <div className={styles.separator} />
            <div className={styles['sessions-wrapper']}>
                <div className={styles['tabs-wrapper']}>
                    <Tabs
                        type="filled"
                        activeIndex={activeSessionTab}
                        onTabChange={handleSessionTabChange}
                        className={styles.tabs}>
                        <Tab label="My projects" value="mySessions"></Tab>
                        <Tab label="Shared" value="shared"></Tab>
                        <Tab label="Favorites" value="favorites"></Tab>
                    </Tabs>
                    <Tabs
                        type="filled"
                        activeIndex={activeLayoutTab}
                        onTabChange={handleLayoutTabChange}
                        className={styles.tabs}>
                        <Tab icon="icon-grid-view-outline" value="grid" disabled={true}></Tab>
                        <Tab icon="icon-list-view-outline" value="list"></Tab>
                    </Tabs>
                </div>
                <div className={styles['controls-wrapper']}>
                    <Search
                        searchType="secondary"
                        searchValue={searchValue}
                        onClear={() => setSearchValue('')}
                        onChange={handleSearch}
                    />
                    <div className={styles.sort}>
                        <Select
                            className={styles['design-system-filter']}
                            data={designSystemList}
                            placeholder="Design system"
                            onSelect={handleDesignSystemFilter}
                            value={designSystemFilter.value}
                            suffixTemplate={(item) => {
                                return item.iconName && <DesignSystemBadge {...item}></DesignSystemBadge>;
                            }}
                            required={false}></Select>
                        <Select
                            className={styles['sort-selector']}
                            data={sortList}
                            value={sortBy.value}
                            onSelect={handleSortBySelect}
                            placeholder="Sort by"
                            required={false}></Select>
                    </div>
                </div>
                <div className={styles['session-wrapper']}>
                    {activeLayoutTab === 0 && (
                        <div className={styles.grid}>
                            {activeSessions.map((session, index) => (
                                <SessionCard
                                    generateImage={true}
                                    thumbnail={session.thumbnail}
                                    ref={sessionRef as any}
                                    key={index}
                                    userEmail={userEmail}
                                    sessionId={session.sessionId}
                                    sessionName={session.topic}
                                    lastEdited={session.updatedAt}
                                    favorite={session.favorite}
                                    designSystem={session.designSystem}
                                    ownerEmail={session.ownerEmail}
                                    isSharedSession={activeSessionTab === 1}
                                    onToggleFavorite={() => handleToggleFavorite(index)}
                                    onClickSession={() => handleSessionClick(session)}
                                    onUpdateSessionName={(topic: any) => handleUpdateSessionName(topic, index)}
                                    onDeleteSession={handleDeleteSession}
                                    onDuplicateSession={handleDuplicateSession}
                                />
                            ))}
                        </div>
                    )}
                    {activeLayoutTab === 1 && (
                        <div className={styles.list}>
                            {activeSessions.map((session, index) => (
                                <SessionListItem
                                    key={index}
                                    userEmail={userEmail}
                                    ref={sessionRef as any}
                                    sessionId={session.sessionId}
                                    sessionName={session.topic}
                                    lastEdited={session.updatedAt}
                                    favorite={session.favorite}
                                    ownerEmail={session.ownerEmail}
                                    designSystem={session.designSystem}
                                    isSharedSession={activeSessionTab === 1}
                                    onToggleFavorite={() => handleToggleFavorite(index)}
                                    onClickSession={() => handleSessionClick(session)}
                                    onUpdateSessionName={(topic: any) => handleUpdateSessionName(topic, index)}
                                    onDeleteSession={handleDeleteSession}
                                    onDuplicateSession={handleDuplicateSession}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <Modal visible={showAttachModal} onCancel={() => setShowAttachModal(false)} title="Attach your image file">
                <Upload
                    onChange={handleUploadChange}
                    multiple={false}
                    autoUpload={false}
                    acceptFileType=".JPEG,.JPG,.PNG,.GIF,.WEBP"
                    uploadInstruction="You can attach image files. The max file size is 5MB."
                    maxFileSize={5 * 1024 * 1024}
                    config={{
                        trigger: false,
                        type: 'inline',
                        size: true,
                    }}
                />
            </Modal>
            <Modal
                visible={showAttachFileModal}
                onCancel={() => setShowAttachFileModal(false)}
                title="Attach your data file">
                <Upload
                    onChange={handleDataFileUploadChange}
                    multiple={false}
                    autoUpload={false}
                    acceptFileType=".CSV,.XLSX,.XLS"
                    uploadInstruction="You can attach Excel or CSV files. The max file size is 10MB."
                    maxFileSize={10 * 1024 * 1024}
                    config={{
                        trigger: false,
                        type: 'inline',
                        size: true,
                    }}
                />
            </Modal>
        </div>
    );
};

export default Dashboard;
